/**
 * Call Popup Component
 * ====================
 * Shows real-time popup notifications for incoming calls
 * Uses Socket.IO to receive call events from telephony server
 */

import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { io } from 'socket.io-client';
import config from '../../config/config';

// Telephony server URL
const TELEPHONY_URL = `${window.location.protocol}//${window.location.hostname}:3010`;

// Context for call events
const CallContext = createContext(null);

export const useCallEvents = () => useContext(CallContext);

/**
 * Call Popup Provider - Wrap your app with this
 */
export const CallPopupProvider = ({ userId, extension, userName, children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const audioRef = useRef(null);

  // Initialize Socket.IO connection
  useEffect(() => {
    const newSocket = io(TELEPHONY_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    newSocket.on('connect', () => {
      console.log('Connected to telephony server');
      setIsConnected(true);

      // Register user with extension
      newSocket.emit('register', {
        userId,
        extension,
        userName
      });
    });

    newSocket.on('registered', (data) => {
      console.log('Registered with telephony server:', data);
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from telephony server');
      setIsConnected(false);
    });

    // Handle call events
    newSocket.on('call_event', (event) => {
      handleCallEvent(event);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [userId, extension, userName]);

  // Handle incoming call events
  const handleCallEvent = useCallback((event) => {
    const { type, data, timestamp } = event;

    switch (type) {
      case 'call_start':
      case 'incoming_call':
        // Show popup for incoming call
        setActiveCall({
          ...data,
          type: 'incoming',
          timestamp
        });
        // Play ring sound
        playRingSound();
        // Request notification permission and show desktop notification
        showDesktopNotification(data);
        break;

      case 'call_answered':
        // Update active call status
        setActiveCall(prev => prev ? { ...prev, status: 'answered' } : null);
        stopRingSound();
        break;

      case 'call_ended':
        // Add to history and clear active
        if (activeCall) {
          setCallHistory(prev => [{ ...activeCall, ...data, endedAt: timestamp }, ...prev].slice(0, 50));
        }
        setActiveCall(null);
        stopRingSound();
        break;

      case 'recording_ready':
        // Update call in history with recording
        setCallHistory(prev =>
          prev.map(call =>
            call.callId === data.callId
              ? { ...call, recordingUrl: data.recordingUrl }
              : call
          )
        );
        break;

      default:
        console.log('Unknown call event:', type);
    }
  }, [activeCall]);

  // Ring sound functions
  const playRingSound = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio('/assets/ring.mp3');
      audioRef.current.loop = true;
    }
    audioRef.current.play().catch(console.error);
  };

  const stopRingSound = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  // Desktop notification
  const showDesktopNotification = (callData) => {
    if (!('Notification' in window)) return;

    if (Notification.permission === 'default') {
      Notification.requestPermission();
      return;
    }

    if (Notification.permission === 'granted' && document.hidden) {
      const customerName = callData.customer?.name || 'מספר לא מזוהה';
      const notification = new Notification('שיחה נכנסת', {
        body: `${customerName}\n${callData.callerNumber}`,
        icon: '/assets/phone-icon.png',
        tag: `call-${callData.callId}`,
        requireInteraction: true
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  // Dismiss popup
  const dismissPopup = () => {
    setActiveCall(null);
    stopRingSound();
  };

  // Open customer record
  const openCustomerRecord = (customer) => {
    if (customer?.id && customer?.type) {
      // Navigate to the record view page
      window.location.href = `/lightning/r/${customer.type}/${customer.id}/view`;
    }
  };

  const contextValue = {
    socket,
    isConnected,
    activeCall,
    callHistory,
    dismissPopup,
    openCustomerRecord
  };

  return (
    <CallContext.Provider value={contextValue}>
      {children}
      {activeCall && (
        <CallPopupUI
          call={activeCall}
          onDismiss={dismissPopup}
          onOpenCustomer={() => openCustomerRecord(activeCall.customer)}
        />
      )}
    </CallContext.Provider>
  );
};

/**
 * Call Popup UI Component
 */
const CallPopupUI = ({ call, onDismiss, onOpenCustomer }) => {
  const [timeElapsed, setTimeElapsed] = useState(0);

  // Timer for call duration
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeElapsed(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const customer = call.customer;
  const isIdentified = !!customer;

  return (
    <div style={styles.overlay}>
      <div style={styles.popup}>
        {/* Header */}
        <div style={{
          ...styles.header,
          background: call.status === 'answered'
            ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
            : 'linear-gradient(135deg, #0176d3 0%, #1589ee 100%)'
        }}>
          <div style={styles.headerContent}>
            <span style={styles.phoneIcon}>📞</span>
            <div>
              <div style={styles.headerTitle}>
                {call.status === 'answered' ? 'שיחה פעילה' : 'שיחה נכנסת'}
              </div>
              <div style={styles.headerSubtitle}>
                {formatTime(timeElapsed)}
              </div>
            </div>
          </div>
          <button style={styles.closeBtn} onClick={onDismiss}>✕</button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Customer Info */}
          <div style={styles.customerSection}>
            <div style={{
              ...styles.avatar,
              background: isIdentified
                ? 'linear-gradient(135deg, #0176d3 0%, #1589ee 100%)'
                : '#999'
            }}>
              {isIdentified ? customer.name.charAt(0) : '?'}
            </div>
            <div style={styles.customerInfo}>
              <div style={styles.customerName}>
                {isIdentified ? customer.name : 'לקוח לא מזוהה'}
              </div>
              <div style={styles.customerPhone}>{call.callerNumber}</div>
              {isIdentified && customer.city && (
                <div style={styles.customerCity}>📍 {customer.city}</div>
              )}
            </div>
          </div>

          {/* Customer Type Badge */}
          {isIdentified && (
            <div style={styles.badgeSection}>
              <span style={{
                ...styles.badge,
                background: customer.type === 'Account' ? '#e3f2fd' : customer.type === 'Contact' ? '#f3e5f5' : '#fff3e0',
                color: customer.type === 'Account' ? '#1976d2' : customer.type === 'Contact' ? '#7b1fa2' : '#f57c00'
              }}>
                {customer.type === 'Account' ? '🏢 חשבון' : customer.type === 'Contact' ? '👤 איש קשר' : '💡 ליד'}
              </span>
            </div>
          )}

          {/* IVR Selection */}
          {call.ivrSelection && (
            <div style={styles.ivrSection}>
              <span style={styles.ivrLabel}>בחירת תפריט:</span>
              <span style={styles.ivrValue}>{call.ivrSelection}</span>
            </div>
          )}

          {/* Extension */}
          {call.extension && (
            <div style={styles.extensionSection}>
              <span style={styles.extensionLabel}>מנותב לשלוחה:</span>
              <span style={styles.extensionValue}>{call.extension}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          {isIdentified && (
            <button style={styles.primaryBtn} onClick={onOpenCustomer}>
              פתח כרטיס לקוח
            </button>
          )}
          <button style={styles.secondaryBtn} onClick={onDismiss}>
            סגור
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    padding: '20px',
    zIndex: 99999,
    animation: 'fadeIn 0.3s ease'
  },
  popup: {
    width: '380px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    overflow: 'hidden',
    animation: 'slideInRight 0.4s ease',
    direction: 'rtl'
  },
  header: {
    padding: '16px 20px',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  phoneIcon: {
    fontSize: '28px',
    animation: 'pulse 1s infinite'
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: '600'
  },
  headerSubtitle: {
    fontSize: '14px',
    opacity: 0.9
  },
  closeBtn: {
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    padding: '20px'
  },
  customerSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px'
  },
  avatar: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '24px',
    fontWeight: 'bold'
  },
  customerInfo: {
    flex: 1
  },
  customerName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '4px'
  },
  customerPhone: {
    fontSize: '16px',
    color: '#666',
    fontFamily: 'monospace',
    direction: 'ltr',
    textAlign: 'right'
  },
  customerCity: {
    fontSize: '14px',
    color: '#888',
    marginTop: '4px'
  },
  badgeSection: {
    marginBottom: '16px'
  },
  badge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '500'
  },
  ivrSection: {
    background: '#f8f9fa',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  ivrLabel: {
    color: '#666',
    fontSize: '14px'
  },
  ivrValue: {
    fontWeight: '600',
    color: '#1a1a1a'
  },
  extensionSection: {
    background: '#f8f9fa',
    padding: '12px 16px',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  extensionLabel: {
    color: '#666',
    fontSize: '14px'
  },
  extensionValue: {
    fontWeight: '600',
    color: '#0176d3'
  },
  actions: {
    padding: '16px 20px',
    borderTop: '1px solid #eee',
    display: 'flex',
    gap: '12px'
  },
  primaryBtn: {
    flex: 1,
    padding: '12px',
    background: 'linear-gradient(135deg, #0176d3 0%, #1589ee 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  secondaryBtn: {
    flex: 1,
    padding: '12px',
    background: '#f8f9fa',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

// Add keyframes animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideInRight {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
`;
document.head.appendChild(styleSheet);

export default CallPopupUI;
